// Editing "the look": the selection's appearance, or (nothing selected) the current appearance new objects get.
import { produce } from 'immer';
import { useDesign } from '../state';
import { useDesignUi } from './designUi';
import { setAppearance } from '../commands/commands';
import { findNode } from '../model/tree';
import type { Appearance, ColorRef, Node, Paint } from '../model/types';
import { defaultStroke } from '../model/defaults';

/** The appearance shown in the panels: the first selected drawable's, else the current one. */
export function shownAppearance(): Appearance {
  const { doc } = useDesign.getState();
  const ui = useDesignUi.getState();
  if (doc)
    for (const id of ui.selection) {
      const f = findNode(doc, id);
      const n = f && firstDrawable(f.node);
      if (n) return n.appearance;
    }
  return ui.current;
}
const firstDrawable = (n: Node): Node | null => {
  if (n.kind !== 'group') return n;
  for (const c of n.children) {
    const r = firstDrawable(c);
    if (r) return r;
  }
  return null;
};

/** Applies `fn` to the selection (one undoable step; `merge` joins a slider drag into one) or to `current`. */
export function editLook(fn: (a: Appearance) => void, merge?: string) {
  const ui = useDesignUi.getState();
  if (ui.selection.length) useDesign.getState().run(setAppearance(ui.selection, fn, merge));
  else ui.set({ current: produce(ui.current, fn) });
}

/** The colour of the active box (fill or stroke) of a look, or null for "none". */
export function targetColor(a: Appearance, target: 'fill' | 'stroke'): ColorRef | null {
  const p = target === 'fill' ? a.fills[0]?.paint : a.strokes[0]?.paint;
  return p?.kind === 'solid' ? p.color : null;
}

/** Sets the active box's colour (adds a fill or stroke if there is none); `null` removes it ("none"). */
export function setTargetColor(c: ColorRef | null, merge?: string) {
  const target = useDesignUi.getState().target;
  editLook((a) => {
    const paint: Paint = c ? { kind: 'solid', color: c } : { kind: 'none' };
    if (target === 'fill') {
      if (!c) a.fills = [];
      else if (a.fills[0]) a.fills[0].paint = paint;
      else a.fills.push({ paint, opacity: 1, blend: 'normal' });
    } else {
      if (!c) a.strokes = [];
      else if (a.strokes[0]) a.strokes[0].paint = paint;
      else a.strokes.push({ ...defaultStroke(), paint });
    }
  }, merge);
}
